import differenceInDays from 'date-fns/differenceInDays';
import { useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import ThemeV2 from '../../../theme';

import assemblyLogo from '../../../img/svgs/assembly-logo-mono.svg';
import assemblyLogoFull from '../../../img/logos/assemblyLogoFull.svg';
import errorCat from '../../../img/svgs/error-cat.svg';
import {
  ASSEMBLING_YOUR_ASSEMBLY,
  CONTACTING_ROBOTS,
  DOCUMENTING_DOCUMENTS,
  GO_BACK_TO_CHROME_STORE,
  ORGANIZING_URLS,
  POWERED_BY,
  PUTTING_ONLINE,
  RETRY_OR_REDOWNLOAD,
  ROBOTS_CONTACTED,
  SOMETHING_WENT_WRONG,
  TAKING_OFFLINE,
  TRY_AGAIN,
  ZEEP_ZOOP_ZOP,
} from '../../../languages/en/external';
import PageNotFound from '../404';
import { GetProfileInfoResponse } from '../../../queries/Profile';
import { GET_PROFILE_INFO } from '../../../constants/endpoints';
import { V2_HOME } from '../../../constants/routes';
import {
  Loader,
  LoaderContainer,
  Wrapper,
  ErrorViewWrapper,
  CatImage,
  ErrorHeading,
  ErrorSubHeading,
  TryAgainButton,
  CreateAssemblyAccountLink,
  ExternalLinkIcon,
  FullAssemblyLogoIcon,
  ContentWrapper,
  StyledImage,
  ChromeStoreButton,
} from './styles';
import { AccountState, ExtDbState } from './types';
import { makeAPICallWithDataReturn } from '../../../queries/utils';
import {
  getChromeExtensionId,
  waitForChromeExtensionId,
} from '../../../Utils/extension';
import Body from '../../atoms/Body';
import { isChrome, isDesktopPlatform, isEdge } from '../../../Utils/window';
import { ExtensionMessageTypes } from '../../../Utils/constants';
import { OptedWorkspaceExperience } from '../../../interfaces/currentUser';
import { NEW_APP_URL } from '../../../config';

const phraseList = [
  TAKING_OFFLINE,
  PUTTING_ONLINE,
  CONTACTING_ROBOTS,
  ZEEP_ZOOP_ZOP,
  ROBOTS_CONTACTED,
  DOCUMENTING_DOCUMENTS,
  ORGANIZING_URLS,
  ASSEMBLING_YOUR_ASSEMBLY,
];

const ExtensionLoading = () => {
  const [extDbState, setExtDbState] = useState<ExtDbState>();
  const [accountState, setAccountState] = useState<AccountState>();
  const [shortcut, setShortcut] = useState<string>();
  const [phraseIndex, setPhraseIndex] = useState<number>(0);
  const [extPort, setExtPort] = useState<chrome.runtime.Port>();
  const pollForDbStatusInterval = useRef<NodeJS.Timeout>();
  const extTimeout = useRef<NodeJS.Timeout>();

  const [
    shouldRedirectToNewWorkspaceDomain,
    setShouldRedirectToNewWorkspaceDomain,
  ] = useState(false);

  useEffect(() => {
    const phraseListLength = phraseList.length;
    const interval = setInterval(() => {
      if (phraseIndex === phraseListLength - 1) {
        setPhraseIndex(0);
      } else {
        setPhraseIndex(phraseIndex + 1);
      }
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [phraseIndex]);

  useEffect(() => {
    const connectWithExtension = async () => {
      const extensionId = await waitForChromeExtensionId();

      if (extensionId) {
        const port = chrome.runtime.connect(extensionId, { name: 'dbStatus' });

        setExtPort(port);

        return () => {
          port.disconnect();
        };
      } else {
        datadogLogs.logger.warn(
          'Timed out looking for injected extension ID.' +
            "It's possible that the user did not have the extension installed",
        );
        window.location.href = V2_HOME;
      }
    };

    connectWithExtension();
  }, []);

  useEffect(() => {
    if (!extDbState && extPort) {
      const interval = setInterval(() => {
        extPort.postMessage({});
      }, 1000);

      const timeout = setTimeout(() => {
        setExtDbState('error');
        clearInterval(interval);
        datadogLogs.logger.warn(
          'Timed out getting database status from browser extension',
        );
      }, 30000);

      extTimeout.current = timeout;
      pollForDbStatusInterval.current = interval;

      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [extDbState, extPort]);

  useEffect(() => {
    if (extPort) {
      const statusMessageHandler = (resp: {
        status: string;
        shortcut?: string;
      }) => {
        if (resp.status === 'success' || resp.status === 'error') {
          setExtDbState(resp.status);
          setShortcut(resp.shortcut);

          if (resp.status === 'success') {
            extPort.onMessage.removeListener(statusMessageHandler);
          }

          if (pollForDbStatusInterval.current) {
            clearInterval(pollForDbStatusInterval.current);
          }

          if (extTimeout.current) {
            clearTimeout(extTimeout.current);
          }
        }
      };

      extPort.onMessage.addListener(statusMessageHandler);

      return () => {
        extPort.onMessage.removeListener(statusMessageHandler);

        if (pollForDbStatusInterval.current) {
          clearInterval(pollForDbStatusInterval.current);
        }

        if (extTimeout.current) {
          clearTimeout(extTimeout.current);
        }
      };
    }
  }, [extPort]);

  useEffect(() => {
    if (!accountState) {
      const getUserAccountInfo = async () => {
        try {
          const profileData: GetProfileInfoResponse =
            await makeAPICallWithDataReturn(GET_PROFILE_INFO);

          if (profileData?.member.activatedAt) {
            const daysSinceAccountCreation = differenceInDays(
              new Date(),
              new Date(profileData.member.activatedAt),
            );

            if (daysSinceAccountCreation > 1) {
              setAccountState('active');
            } else {
              setAccountState('newUser');
            }

            if (
              profileData.member.optedWorkspaceExperience ===
              OptedWorkspaceExperience.New
            ) {
              setShouldRedirectToNewWorkspaceDomain(true);
            }
          } else {
            setAccountState('loggedOut');
          }
        } catch (e) {
          setAccountState('loggedOut');
        }
      };

      getUserAccountInfo();
    }
  }, [accountState]);

  useEffect(() => {
    if (extDbState === 'success') {
      const extensionId = getChromeExtensionId();
      if (shortcut === '') {
        window.location.href = `chrome-extension://${extensionId}/tabs/shortcut.html`;
      } else if (accountState === 'loggedOut') {
        if (extensionId) {
          window.location.href = `chrome-extension://${extensionId}/tabs/onboarding.html`;
        }
      } else if (accountState === 'newUser') {
        window.location.href = V2_HOME;
      } else if (accountState === 'active') {
        if (shouldRedirectToNewWorkspaceDomain) {
          window.location.href = `${NEW_APP_URL}/discover`;
        } else {
          window.location.href = `${V2_HOME}?openModal=true`;
        }
      }
    }
  }, [accountState, extDbState, shouldRedirectToNewWorkspaceDomain, shortcut]);

  if ((!isEdge && !isChrome) || !isDesktopPlatform) {
    return <PageNotFound />;
  }

  if (extDbState === 'error') {
    const retry = async () => {
      const extensionId = getChromeExtensionId();

      if (extensionId) {
        setExtDbState(undefined);
        await chrome.runtime.sendMessage(extensionId, {
          type: ExtensionMessageTypes.RetryDatabaseInitialization,
        });
      }
    };

    return (
      <ErrorViewWrapper>
        <ContentWrapper>
          <CatImage src={errorCat} alt="" />
          <ErrorHeading variant="subHead4" color={ThemeV2.palette.gray9}>
            {SOMETHING_WENT_WRONG}
          </ErrorHeading>
          <ErrorSubHeading variant="subHead1" color={ThemeV2.palette.gray9}>
            {RETRY_OR_REDOWNLOAD}
          </ErrorSubHeading>
          <TryAgainButton
            color="primary"
            icon="sync"
            isFullWidth
            onClick={retry}
            size="medium"
          >
            {TRY_AGAIN}
          </TryAgainButton>
          <ChromeStoreButton
            color="secondary"
            isFullWidth
            onClick={() => {
              window.location.href =
                'https://chrome.google.com/webstore/detail/assembly-universal-workpl/lfifhhbadciimoimgencbhbkdmaigfhn';
            }}
            variant="text"
          >
            {GO_BACK_TO_CHROME_STORE}
          </ChromeStoreButton>
        </ContentWrapper>
        <CreateAssemblyAccountLink href="https://app.joinassembly.com/create-account">
          {POWERED_BY}
          <FullAssemblyLogoIcon src={assemblyLogoFull} alt="Assembly" />
          <ExternalLinkIcon icon="external-link-2" size="16px" />
        </CreateAssemblyAccountLink>
      </ErrorViewWrapper>
    );
  }

  return (
    <Wrapper>
      <LoaderContainer>
        <StyledImage src={assemblyLogo} alt="assembly logo" />
        <Loader />
      </LoaderContainer>
      <Body variant="body1" color={ThemeV2.palette.gray9}>
        {phraseList[phraseIndex]}
      </Body>
    </Wrapper>
  );
};

export default ExtensionLoading;
